import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { Box, Grid, Skeleton, Typography } from '@mui/material';
import { format, getYear } from 'date-fns';
import { useAppSelector } from '@/redux/hooks';
import { selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import { selectFocusCalendarView } from '@/redux/selectors/focusCalendarSelectors';
import { AvailStatus } from '@/components/_common/Table/Calendar/Calendar.types';
import { getColorByAvailStatus } from '@/components/_common/Table/Calendar/utils';
import { FocusSchedulingStats } from '@/redux/slices/slice_types';
import { useQuery } from '@/components/_hooks/useQuery';
import { parseStartDateQueryParam } from '@/_utils/date';
import { FocusTheme } from '@/components/Focus/configs/theme';

interface Props {
  stats: FocusSchedulingStats[] | null;
}

const StatusPanel: React.FC<Props> = ({ stats }) => {
  const calendarStartDateParam = useQuery().get('calendarStartDate');
  const filters = useAppSelector(selectDataTableFilters);
  const view = useAppSelector(selectFocusCalendarView);
  const statsCountByStatus = useMemo(() => (stats ? countByStatus(stats) : null), [stats]);
  const totalCount =
    useMemo(
      () =>
        stats?.reduce((acc, cur) => {
          return acc + cur.stats.total_count;
        }, 0),
      [stats]
    ) ?? null;
  const startDate = new Date(filters.term_start.term_start__gte);
  const endDate = new Date(filters.term_start.term_start__lte);
  const startYear = getYear(startDate);
  const endYear = getYear(endDate);
  const singleDayFormat = 'EEE, MMMM d, yyyy';
  const monthFormat = 'MMMM yyyy';
  const rangeStartDayFormat = `EEE, MMMM d${startYear === endYear ? '' : ', yyyy'}`;
  const rangeEndDayFormat = 'EEE, MMMM d, yyyy';

  let dateFormatted: string | null = null;

  if (view === 'day') {
    dateFormatted = format(startDate, singleDayFormat);
  } else if (view === 'week') {
    dateFormatted = `${format(startDate, rangeStartDayFormat)} to ${format(endDate, rangeEndDayFormat)}`;
  } else if (view === 'month' && calendarStartDateParam) {
    dateFormatted = format(parseStartDateQueryParam(calendarStartDateParam), monthFormat);
  }

  return (
    <StatusPanelWrapper>
      <Grid container gap={0.5} alignItems="center">
        <Grid item xs>
          <FormattedDateTypography variant="h4">{dateFormatted}</FormattedDateTypography>
          {totalCount === null ? (
            <Skeleton width={150} />
          ) : (
            <AvailsTypography>
              Showing {totalCount.toLocaleString()} avail{totalCount > 1 ? 's' : ''} total
            </AvailsTypography>
          )}
        </Grid>

        <StatusItem label="Ready" value={statsCountByStatus ? statsCountByStatus[AvailStatus.Success] : null} status={AvailStatus.Success} />
        <StatusItem label="Pending" value={statsCountByStatus ? statsCountByStatus[AvailStatus.Warning] : null} status={AvailStatus.Warning} />
        {/* <StatusItem
          label="Attention"
          value={statsCountByStatus ? statsCountByStatus[AvailStatus.Error] : null}
          status={AvailStatus.Error}
        /> */}
      </Grid>
    </StatusPanelWrapper>
  );
};

export default StatusPanel;

const StatusPanelWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  background-color: ${({ theme }) => theme.palette.background.paper};
`;

const FormattedDateTypography = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  color: ${({ theme }) => theme.customOverrides.Calendar.textPrimary};
`;

const AvailsTypography = styled(Typography)`
  color: ${({ theme }) => theme.customOverrides.Calendar.textSecondary};
`;

const countByStatus = (stats: FocusSchedulingStats[]) => {
  return stats.reduce<Record<AvailStatus, number>>(
    (acc, stat) => {
      acc[AvailStatus.Success] += stat.stats.status_ready_count;
      acc[AvailStatus.Warning] += stat.stats.status_pending_count;
      acc[AvailStatus.Error] += stat.stats.status_error_count;
      return acc;
    },
    {
      [AvailStatus.Success]: 0,
      [AvailStatus.Warning]: 0,
      [AvailStatus.Error]: 0
    }
  );
};

const StatusItem = ({ label, value, status }: { status: AvailStatus; value: number | null; label: string }) => {
  return (
    <div style={{ position: 'relative' }}>
      {value === null && <StatusItemSkeleton />}
      <StatusItemWrapper item sx={{ opacity: value === null ? 0 : 1 }} status={status}>
        <StatusItemLabel>
          <Box flexGrow={1}>{label}</Box>
          <Bullet status={status} />
        </StatusItemLabel>
        <StatusItemValue>{(value ?? 0).toLocaleString()}</StatusItemValue>
      </StatusItemWrapper>
    </div>
  );
};

const StatusItemSkeleton = styled(Skeleton)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: none;
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px`};
`;

const StatusItemWrapper = styled(Grid)<{ status: AvailStatus }>`
  min-width: 100px;
  padding: ${({ theme }) => theme.spacing(1, 1.5)};
  border: 2px solid ${({ theme }) => theme.customOverrides.Calendar.backgroundDefault};
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px`};
  background-color: ${({ theme }) => (theme.palette.mode === 'dark' ? theme.customOverrides.Calendar.background : theme.palette.background.default)};
  font-weight: 700;
  color: ${({ theme, status }) => getColorByAvailStatus(status, theme.palette.mode === 'dark')};
`;

const StatusItemLabel = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.857rem;
`;

const Bullet = styled(Box)<{ status: AvailStatus }>`
  width: 11px;
  height: 11px;
  border: 2px solid
    ${({ theme }) => (theme.palette.mode === 'dark' ? theme.customOverrides.Calendar.backgroundAlt : (theme as FocusTheme).palette.neutral[10])};
  border-radius: 50%;
  background-color: ${({ status, theme }) => getColorByAvailStatus(status, theme.palette.mode === 'dark')};
`;

const StatusItemValue = styled.div`
  font-size: 1.643rem;
  line-height: 2.143rem;
`;
